import { create } from 'zustand'

/**
 * Очередь уведомлений.
 *
 * Здесь Zustand на месте, а в остальном кабинете — почти нигде. Разница
 * простая: профиль, часы работы и компания живут на сервере, и их
 * состоянием управляет React Query — он сам знает, когда данные устарели,
 * и не даёт двум копиям разойтись. Уведомления на сервере не живут
 * вообще; их порождает любой обработчик из любого места, а показывает
 * один компонент в корне. Это ровно то состояние, ради которого нужен
 * внешний стор: без него пришлось бы протаскивать колбэк через полдерева.
 *
 * Класть сюда данные с сервера — распространённая ошибка. Копия в сторе
 * не протухает сама, и через месяц выясняется, что на одном экране
 * компания старая, а на другом новая.
 */

export type ToastKind = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
}

interface ToastState {
  toasts: Toast[]
  push: (kind: ToastKind, message: string) => void
  dismiss: (id: number) => void
}

/** Время показа. Ошибка висит дольше: её ещё нужно прочитать и понять. */
const LIFETIME: Record<ToastKind, number> = {
  success: 2600,
  info: 3600,
  error: 6000,
}

let nextId = 1

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (kind, message) => {
    const id = nextId++
    set((state) => ({ toasts: [...state.toasts, { id, kind, message }] }))

    window.setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
    }, LIFETIME[kind])
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}))

/**
 * Короткий доступ без подписки на состояние.
 *
 * Обработчику нужно только отправить уведомление. Подпишись он через
 * хук — и компонент начнёт перерисовываться на каждое чужое сообщение.
 */
export const toast = {
  success: (message: string) => useToastStore.getState().push('success', message),
  error: (message: string) => useToastStore.getState().push('error', message),
  info: (message: string) => useToastStore.getState().push('info', message),
}
